import React, { useEffect, useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import FormControlLabel from "@mui/material/FormControlLabel";
import MenuItem from "@mui/material/MenuItem";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { useDispatch, useSelector } from "react-redux";
import { BlogBreadcrumbs } from "../components/BlogBreadcrumbs";
import CustomModal from "../../../common/customModal/CustomModal";
import { AdminTable, ActionCell } from "../../../common/adminTable";
import { useConfirm } from "../../../common/confirmDialog/useConfirm";
import { fetchRedirects, createRedirect, updateRedirect, deleteRedirect } from "../redirectSlice";
import { inputSx } from "../blogTheme";

const EMPTY = { from: "", to: "", statusCode: 301, isActive: true };
const rowsPerPage = 20;

const switchSx = {
  "& .MuiSwitch-switchBase.Mui-checked": { color: "#065F46" },
  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { bgcolor: "#065F46" },
};

export default function RedirectManager() {
  const dispatch = useDispatch();
  const { redirects, total, page, loading, error } = useSelector((state) => state.redirect);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [deletingId, setDeletingId] = useState(null);
  const confirm = useConfirm();

  useEffect(() => { dispatch(fetchRedirects({ page: 1, limit: rowsPerPage })); }, [dispatch]);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setDialogOpen(true); };
  const openEdit = (r) => {
    setEditing(r);
    setForm({ from: r.from, to: r.to, statusCode: r.statusCode, isActive: r.isActive });
    setDialogOpen(true);
  };

  const handleSave = () => {
    if (editing) dispatch(updateRedirect({ id: editing._id, data: form }));
    else dispatch(createRedirect(form));
    setDialogOpen(false);
  };

  const handleToggleActive = (r) => {
    dispatch(updateRedirect({ id: r._id, data: { isActive: !r.isActive } }));
  };

  const handleDelete = async (id) => {
    const ok = await confirm({
      title: "Delete Redirect",
      description: "Are you sure you want to delete this redirect?",
      confirmText: "Delete",
      destructive: true,
    });
    if (!ok) return;
    setDeletingId(id);
    await dispatch(deleteRedirect(id));
    setDeletingId(null);
  };

  const columns = [
    {
      field: "from",
      headerName: "From",
      minWidth: 180,
      cellSx: { maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
      renderCell: (r) => r.from,
    },
    {
      field: "to",
      headerName: "To",
      minWidth: 180,
      cellSx: { maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
      renderCell: (r) => r.to,
    },
    {
      field: "statusCode",
      headerName: "Code",
      width: 80,
      renderCell: (r) => r.statusCode,
    },
    {
      field: "isActive",
      headerName: "Active",
      width: 90,
      renderCell: (r) => (
        <Switch
          checked={!!r.isActive}
          onChange={() => handleToggleActive(r)}
          size="small"
          sx={switchSx}
        />
      ),
    },
    {
      field: "__actions",
      headerName: "",
      width: 90,
      align: "right",
      renderCell: (r) => (
        <ActionCell
          actions={[
            {
              label: "Edit",
              icon: <EditIcon fontSize="small" />,
              onClick: () => openEdit(r),
            },
            {
              label: "Delete",
              icon: <DeleteIcon fontSize="small" />,
              color: "#EA001B",
              onClick: () => handleDelete(r._id),
              loading: deletingId === r._id,
            },
          ]}
        />
      ),
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <BlogBreadcrumbs items={[{ label: "Blog", href: "/blog" }, { label: "Redirects" }]} />

      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: "#362B24", fontFamily: "Heebo" }}>
          Redirects
        </Typography>
        <Button
          startIcon={<AddIcon />}
          onClick={openCreate}
          sx={{
            background: "linear-gradient(180deg, #065F46 0%, #0D7B5C 100%)",
            color: "#fff",
            borderRadius: "29px",
            boxShadow: "1px 2px 30px 0px rgba(0,0,0,0.06)",
            fontFamily: "Heebo",
            textTransform: "none",
            px: 3,
            "&:hover": { opacity: 0.9 },
          }}
        >
          Add Redirect
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <AdminTable
        columns={columns}
        rows={redirects}
        loading={loading}
        rowKey="_id"
        emptyText="No redirects found"
        page={page}
        rowsPerPage={rowsPerPage}
        totalCount={total}
        onPageChange={(pg) => dispatch(fetchRedirects({ page: pg, limit: rowsPerPage }))}
        deletingId={deletingId}
      />

      {/* Create / Edit dialog — logic unchanged */}
      <CustomModal open={dialogOpen} onClose={() => setDialogOpen(false)}>
        <Box sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: "#362B24", fontFamily: "Heebo", mb: 2.5 }}>
            {editing ? "Edit Redirect" : "New Redirect"}
          </Typography>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <TextField
              label="From (path)"
              value={form.from}
              onChange={(e) => setForm({ ...form, from: e.target.value })}
              size="small"
              fullWidth
              required
              sx={inputSx}
            />
            <TextField
              label="To (path)"
              value={form.to}
              onChange={(e) => setForm({ ...form, to: e.target.value })}
              size="small"
              fullWidth
              required
              sx={inputSx}
            />
            <TextField
              label="Status Code"
              value={form.statusCode}
              onChange={(e) => setForm({ ...form, statusCode: Number(e.target.value) })}
              size="small"
              fullWidth
              select
              sx={inputSx}
            >
              <MenuItem value={301}>301 — Permanent</MenuItem>
              <MenuItem value={302}>302 — Temporary</MenuItem>
            </TextField>
            <FormControlLabel
              control={
                <Switch
                  checked={form.isActive}
                  onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                  sx={switchSx}
                />
              }
              label={<Typography sx={{ fontFamily: "Heebo", color: "#362B24" }}>Active</Typography>}
            />
          </Box>

          <Box sx={{ display: "flex", gap: 1.5, justifyContent: "flex-end", mt: 3 }}>
            <Button
              onClick={() => setDialogOpen(false)}
              sx={{
                borderRadius: "28px", height: "41px", px: 3,
                border: "1px solid #EBEBEB", color: "#362B24",
                fontFamily: "Heebo", textTransform: "none",
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={!form.from || !form.to}
              sx={{
                borderRadius: "28px", height: "41px", px: 3,
                background: "linear-gradient(180deg, #065F46 0%, #0D7B5C 100%)",
                color: "#fff", fontFamily: "Heebo", textTransform: "none",
                boxShadow: "1px 2px 30px 0px rgba(0,0,0,0.06)",
                "&:hover": { opacity: 0.9 },
                "&:disabled": { opacity: 0.5, color: "#fff" },
              }}
            >
              Save
            </Button>
          </Box>
        </Box>
      </CustomModal>
    </Box>
  );
}
